import getTransactionReportParams from '@/utils/helpers/GetTransactionReportParams'
import { buildUpdatePromiseIterable } from '@/utils/helpers/BuildPromiseIterable'
import { notify } from '@/utils/helpers/Notify'
import handleXHRError from '@/utils/helpers/HandleXHRError'

/**
 * Sets the report's template type (from the OPTIONS call to that report endpoint) in the query along with
 * the cached parameters id
 * @param self
 * @param endpoint
 * @param payload
 * @return {*}
 */
export function setTemplate (self, endpoint, payload) {
  const opts = self.$store.getters.getReportOptions(`reports/${endpoint}`)
  if (opts && opts.template_parameter && opts.templates && Object.keys(opts.templates).length) {
    payload.query[opts.template_parameter] = Object.keys(opts.templates)[0]
  }
  return payload
}

/*
* Changes the status for a single transaction and generates an audit report
* @param {VueComponent} self
* @param {Object} t
* @return void
*/
export function createAuditReport (self, t) {
  const data = getTransactionReportParams(self, [t])
  self.ready = true
  self.disableAuditBtn = true
  const endpoint = 'audit/transaction'
  let opts = self.$store.getters.getReportOptions(`reports/${endpoint}`)
  let promises = [ self.$store.dispatch('cacheParams', { data }) ]
  if (!opts) {
    promises.push(self.$store.dispatch('fetchFormFields', `reports/${endpoint}`))
  }
  return self.$store.dispatch('updateTransactionSelection', [t])
    .then(() => Promise.all(promises))
    .then(r => {
      if (!(r && r.length)) return Promise.reject('Error caching parameters for audit report')
      const cachedParamsResponse = r.find(v => v.id && v.hasOwnProperty('data'))
      const { id, reportId } = cachedParamsResponse
      const payload = setTemplate(self, endpoint, { query: { cached_parameters: id }, type: endpoint, reportId })
      self.ready = false
      if (self.isSaving) self.isSaving = false
      return self.$store.dispatch('startRenderingReport', payload)
    })
    .then(() => {
      self.disableAuditBtn = false
    })
    .catch(e => {
      console.log(e)
      notify(self, 'error', handleXHRError(e || 'Error generating audit report'))
      self.ready = false
      if (self.isSaving) self.isSaving = false
      self.disableAuditBtn = false
      return e
    })
}

/*
* Changes the status for a single transaction and generates a posting report
* @param {VueComponent} self
* @param {Object} t
* @return void
*/
export function createPostingReport (self, t) {
  self.$store.dispatch('updateTransactionSelection', [t])
  const data = getTransactionReportParams(self, [t])
  self.disablePostBtn = true
  self.ready = true
  const endpoint = 'posting/transaction'
  let opts = self.$store.getters.getReportOptions(`reports/${endpoint}`)
  let promises = buildUpdatePromiseIterable(self.$store, [t], { status: 'POSTED' })
  if (!opts) {
    promises.push(self.$store.dispatch('fetchFormFields', `reports/${endpoint}`))
  }
  return Promise.all(promises)
    .then(r => {
      if (!(r && r.length && r[0].id)) return Promise.reject('Failed to set transaction status to POSTED')
      notify(self, 'success', 'Transaction posted')
      // if (t.transactionType === 'Accounts Payable') {
      //   self.$store.dispatch('changeModalVisibility', { entityType: 'remainderModal', visible: true })
      // }
      self.ready = false
      if (self.isSaving) self.isSaving = false
      return self.$store.dispatch('cacheParams', { data })
    })
    .then(r => {                                // start rendering report
      if (!(r && r.id)) return Promise.reject('Error caching parameters for posting report')
      const { id, reportId } = r
      const payload = setTemplate(self, endpoint, { query: { cached_parameters: id }, type: endpoint, reportId })
      return self.$store.dispatch('startRenderingReport', payload)
    })
    .then(() => {
      self.disablePostBtn = false
    })
    .catch(e => {
      console.log(e)
      notify(self, 'error', handleXHRError(e || 'Error generating posting report'))
      self.ready = false
      if (self.isSaving) self.isSaving = false
      self.disablePostBtn = false
      return e
    })
}

/*
* Prints a PDF copy of a bid or bill. If a status is passed, changes the AR's status to the value provided.
* @param {VueComponent} self
* @param {Object} t
* @return void
*/
export async function createBillPdf (self, t, status = null) {
  const stopSpinner = () => {
    if (self.isSaving) self.isSaving = false
  }
  const setFlags = ({ auditBtn = null, postBtn = null, printBtn = null } = {}) => {
    self.disablePostBtn = postBtn
    self.disableAuditBtn = auditBtn
    self.printingOnly = printBtn
  }
  self.ready = true
  if (status === 'AUDITED') {
    setFlags({ auditBtn: t.id })
  } else if (status === 'POSTED') {
    setFlags({ postBtn: t.id })
  } else if (!status) {
    setFlags({ printBtn: t.id })
  }
  const endpoint = `receivables/bidbill/pdf?transaction=${t.id}`
  const opts = () => self.$store.getters.getReportOptions(`reports/${endpoint}`)
  let promises = status ? [self.$store.dispatch('updateAr', { id: t.id, data: { status } })] : []
  if (!opts()) promises.push(self.$store.dispatch('fetchFormFields', `reports/${endpoint}`))
  try {
    const response = await Promise.all(promises)
    if (status && !(response && response.length)) throw new Error(`Failed to set transaction status to ${status}`)
    if (status) notify(self, 'success', `Transaction ${status.toLowerCase()}`)
    self.ready = false
    stopSpinner()
    const cps = await cacheParams(self.$store, { transaction: t.id })
    const cachedParamPayload = {
      query: { transaction: t.id, cached_parameters: cps.id },
      type: `receivables/bidbill/pdf`,
      reportId: cps.reportId
    }
    const payload = setTemplate(self, endpoint, cachedParamPayload)
    await self.$store.dispatch('startRenderingReport', payload)
    setFlags()
  } catch (e) {
    console.log(e)
    notify(self, 'error', handleXHRError(e || 'Error generating AR bid/bill pdf'))
    self.ready = false
    stopSpinner()
    setFlags()
    return e
  }
}

async function cacheParams (store, data) {
  try {
    const cpResponse = await store.dispatch('cacheParams', { data })
    if (!(cpResponse && cpResponse.id)) {
      throw new Error('Error caching parameters')
    }
    return cpResponse
  } catch (e) {
    console.log(e)
    return e
  }
}

/*
* Changes the status for a group of transactions and generates an audit report
* @param {VueComponent} self
* @param {Object} t
* @return void
*/
export function batchCreateAuditReport (self) {
  self.batchUpdateSelection = null
  setDisabledOption(self, 1, true)
  const data = getTransactionReportParams(self, self.filteredSelection)
  self.disableAuditBtn = true
  const endpoint = 'audit/transaction'
  let opts = self.$store.getters.getReportOptions(`reports/${endpoint}`)
  let promises = [ self.$store.dispatch('cacheParams', { data }) ]
  if (!opts) {
    promises.push(self.$store.dispatch('fetchFormFields', `reports/${endpoint}`))
  }
  return Promise.all(promises) // Render report, then send updated status (PUT)
    .then(r => {
      if (!(r && r.length)) return Promise.reject('Error caching parameters for audit report')
      const cachedParamsResponse = r.find(v => v.id && v.hasOwnProperty('data'))
      const { id, reportId } = cachedParamsResponse
      const payload = setTemplate(self, endpoint, { query: { cached_parameters: id }, type: endpoint, reportId })
      setDisabledOption(self, 1, false)
      return self.$store.dispatch('startRenderingReport', payload)
    })
    .then(() => {
      self.disableAuditBtn = false
    })
    .catch(e => {
      console.log(e)
      notify(self, 'error', handleXHRError(e || 'Error generating audit report'))
      setDisabledOption(self, 1, false)
      self.disableAuditBtn = false
      return e
    })
}

/*
* Changes the status for a group of transactions and generates a posting report
* @param {VueComponent} self
* @param {Object} t
* @return void
*/
export function batchCreatePostingReport (self) {
  self.batchUpdateSelection = null
  setDisabledOption(self, 0, true)
  self.disableAuditBtn = true
  const data = getTransactionReportParams(self, self.filteredSelection)
  const endpoint = 'posting/transaction'
  let opts = self.$store.getters.getReportOptions(`reports/${endpoint}`)
  let promises = buildUpdatePromiseIterable(self.$store, self.filteredSelection, { status: 'POSTED' })
  if (!opts) {
    promises.push(self.$store.dispatch('fetchFormFields', `reports/${endpoint}`))
  }
  return Promise.all(promises)                  // Update transaction status to "POSTED"
  .then(r => {                                // then cache params
    if (!(r && r.length && r.length >= self.filteredSelection.length)) return Promise.reject('Error posting transactions')
    notify(self, 'success', 'Transactions Posted')
    // if (data.accountspayable) {
    //   self.$store.dispatch('changeModalVisibility', { entityType: 'remainderModal', visible: true })
    // }
    setDisabledOption(self, 0, false)
    return self.$store.dispatch('cacheParams', { data })
  })
  .then(r => {                                // then start rendering report
    if (!(r && r.id)) return Promise.reject('Error caching parameters for posting report')
    const { id, reportId } = r
    const payload = setTemplate(self, endpoint, { query: { cached_parameters: id }, type: endpoint, reportId })
    return self.$store.dispatch('startRenderingReport', payload)
  })
  .then(() => {
    self.disableAuditBtn = false
  })
  .catch(e => {
    console.log(e)
    notify(self, 'error', handleXHRError(e || 'Error generating posting report'))
    setDisabledOption(self, 0, false)
    self.disableAuditBtn = false
    return e
  })
}

function setDisabledOption (self, index, status) {
  if (index > -1 && self.batchUpdateOptions[index]) {
    self.batchUpdateOptions[index].disabled = status
  }
}



// WEBPACK FOOTER //
// ./src/utils/helpers/DataEntryViews/CreateReport.js